'use client';

import { useState } from 'react';
import { Content } from '@prismicio/client';
import { Disclosure } from '@/components';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';

/**
 * Props for `MultiTab`.
 */
export type MultiTabProps = SliceComponentProps<Content.MultiTabSlice>;

/**
 * Component for "MultiTab" Slices.
 */
const MultiTab = ({ slice }: MultiTabProps): JSX.Element => {
  const {
    primary: {
      icon,
      title,
      description,
      button_label: buttonLabel,
      button_link: buttonLink,
      items,
    },
  } = slice;

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const handleToggle = (index: number) => {
    setOpenIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="max-w-screen-2xl mx-auto px-5 md:px-16 py-16 md:py-28"
    >
      <div className="card text-white flex flex-col lg:flex-row items-center">
        <div className="flex flex-col gap-y-6 p-6 lg:p-16 w-full lg:w-3/5">
          <PrismicNextImage field={icon} width={42} height={42} />
          <PrismicRichText field={title} />
          <PrismicRichText field={description} />
          <div className="flex">
            <PrismicNextLink field={buttonLink} className="button-bordered">
              <span>{buttonLabel}</span>
            </PrismicNextLink>
          </div>
        </div>
        <div className="w-full lg:w-2/5">
          {items.map((item, index) => (
            <Disclosure
              key={`accordion-item-${index}`}
              isOpen={openIndex === index}
              onClick={() => handleToggle(index)}
              disclosureTitle={item.item_title}
              disclosurePanel={item.item_description}
              disclosureClassName="w-full border-white border-solid p-6 border-t-[1px] lg:border-t-0 lg:border-b-[1px] lg:border-l-[1px] lg:first:rounded-tr-[20px] lg:last:rounded-br-[20px] lg:last:border-b-0"
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default MultiTab;
